import { useMutation, useQuery } from '@apollo/client';
import { userPaymentQuery } from '../../graphql/queries';
import {
  deleteSourceMutation,
  setVytalCreditsMutation,
} from '../../graphql/mutations';
import i18n from '@/utils/i18n';
import { IndicatorButton } from '../../components/IndicatorButton';
import { useState } from 'react';
import { Button } from 'reactstrap';
import { formatPrice } from '@/utils/formatter';
import { LoadingContainer, ResponsiveTable } from '../../components/Layout';
import { FaTrashAlt } from 'react-icons/fa';

type PaymentDetailProps = {
  pickpackId: string | null;
};

interface IGetPaymentDetailsResponse {
  vytal_credits: number;
  payment_details: IGlypoUser;
}

interface IGlypoUser {
  paypal_id: string;
  stripe_id: string;
  default_source: IStripeSource;
  sources: IStripeSource[];
}

interface IStripeSource {
  id: string;
  object: string;
  fingerprint: string;
  brand: string;
  last4: string;
  name: string;
  type: string;
}

export const PaymentDetails = function PaymentDetails({
  pickpackId,
}: PaymentDetailProps) {
  const [getNewVytalCredits, setNewVytalCredits] = useState('0');

  const { data = { user: null }, loading } = useQuery(userPaymentQuery, {
    variables: { id: pickpackId },
    onCompleted: (userData) => {
      // @ts-ignore
      setNewVytalCredits(userData.user.vytal_credits.toString());
    },
  });

  const [deleteSource, { loading: deletingSource }] =
    useMutation(deleteSourceMutation);
  const [setVytalCredits, { loading: settingVytalCredits }] = useMutation(
    setVytalCreditsMutation,
  );

  const onSetVytalCreditsClick = async () => {
    if (data && pickpackId) {
      const amount = parseInt(getNewVytalCredits, 10);
      if (!Number.isNaN(amount) && Number.isInteger(amount) && amount >= 0) {
        if (
          !confirm(
            "Are you sure you want to set the vytal credits to '" +
              getNewVytalCredits +
              "'?",
          )
        ) {
          return;
        }
        await setVytalCredits({
          variables: { user_id: pickpackId, vytal_credits: amount },
          refetchQueries: [
            {
              query: userPaymentQuery,
              variables: {
                id: pickpackId,
              },
            },
          ],
        });
      } else {
        alert(
          'Vytal credits must be an integer greater zero but was: ' +
            getNewVytalCredits,
        );
      }
    } else {
      const jsonData = JSON.stringify(data);
      console.error(
        'Data or pickpack id not present. Data: ' +
          jsonData +
          ', pickpack id: ' +
          pickpackId,
      );
    }
  };

  async function onSetDeleteSourceClick(sourceId: string) {
    await deleteSource({
      variables: { user_id: pickpackId, source_id: sourceId },
      refetchQueries: [
        {
          query: userPaymentQuery,
          variables: {
            id: pickpackId,
          },
        },
      ],
    });
  }

  // @ts-ignore
  const paymentDetails: any = data.user && data.user?.payment_details;
  const stripeId = paymentDetails?.stripe_id;
  const defaultSourceId = paymentDetails?.default_source?.id;

  return (
    <div>
      {loading && <LoadingContainer />}
      {data && !loading && (
        <div>
          <br />
          <h4>{i18n.t('Payment:vytalCredits')}</h4>
          <div className="input-group input-group-sm mb-2">
            <span className="input-group-text" id="inputGroup-sizing-sm">
              {i18n.t('Payment:vytalCredits')} (
              {formatPrice(Number(getNewVytalCredits))})
            </span>
            <input
              type="number"
              className="form-control"
              min="0"
              value={getNewVytalCredits}
              onChange={(e) => setNewVytalCredits(e.target.value)}
            />
          </div>
          <div className="input-group input-group-sm mb-3">
            <IndicatorButton
              onClick={() => onSetVytalCreditsClick()}
              disabled={!data}
              isLoading={settingVytalCredits}
            >
              Set Vytal Credits
            </IndicatorButton>
          </div>

          <br />
          <h4>{i18n.t('Payment:paymentSources')}</h4>
          <ResponsiveTable>
            <thead>
              <tr>
                <th>{i18n.t('Common:default')}</th>
                <th>{i18n.t('Common:id')}</th>
                <th>{i18n.t('Payment:providerAccount')}</th>
                <th>{i18n.t('Common:type')}</th>
                <th>{i18n.t('Payment:brand')}</th>
                <th>{i18n.t('Payment:last4')}</th>
                <th>{i18n.t('Payment:object')}</th>
                <th>{i18n.t('Common:delete')}</th>
              </tr>
            </thead>
            <tbody>
              {paymentDetails &&
                paymentDetails.sources
                  .filter((source: any) => source)
                  .map((source: IStripeSource) => {
                    return (
                      <tr key={source.id} style={{ cursor: 'pointer' }}>
                        <td>{source.id === defaultSourceId && '✔️'}</td>
                        <td>{source.id}</td>
                        <td>
                          {source.type === 'paypal' ? (
                            <a
                              target="_blank"
                              href={
                                'https://www.braintreegateway.com/merchants/9m9qgfnwqth3q7vr/customers/' +
                                pickpackId
                              }
                              rel="noreferrer"
                            >
                              Braintree User
                            </a>
                          ) : (
                            <a
                              target="_blank"
                              href={
                                'https://dashboard.stripe.com/customers/' +
                                stripeId
                              }
                              rel="noreferrer"
                            >
                              Stripe {stripeId}
                            </a>
                          )}
                        </td>
                        <td>{source.type}</td>
                        <td>{source.brand}</td>
                        <td>{source.last4}</td>
                        <td>{source.object}</td>
                        <td>
                          <Button
                            className="ms-2"
                            color="danger"
                            size="sm"
                            onClick={() => onSetDeleteSourceClick(source.id)}
                          >
                            <FaTrashAlt />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
              {(!paymentDetails || paymentDetails.sources.length === 0) && (
                <tr>
                  <td colSpan={8}>No payment sources found</td>
                </tr>
              )}
            </tbody>
          </ResponsiveTable>
        </div>
      )}
    </div>
  );
};
